import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Filler, Tooltip, Legend
} from 'chart.js';
import { fmtDec, MetricCard, GC, TC } from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function YearOverYear({ data, user, isAdmin }) {
  const { yearOverYear } = data;
  if (!yearOverYear) return (
    <div className="rep-welcome"><h2>Year-over-year</h2><p style={{color:'var(--text3)'}}>No comparison data available yet.</p></div>
  );

  const { monthly2025 = {}, monthly2026 = {} } = yearOverYear;

  const vals2025 = MONTHS.map((_, i) => monthly2025[i + 1] || 0);
  const vals2026 = MONTHS.map((_, i) => monthly2026[i + 1] || 0);

  const total2025 = vals2025.reduce((s, v) => s + v, 0);
  const total2026 = vals2026.reduce((s, v) => s + v, 0);
  const diff = total2026 - total2025;
  const diffPct = total2025 > 0 ? ((diff / total2025) * 100).toFixed(1) : '—';

  // Only show months that have data in either year
  const activeMonths = MONTHS.filter((_, i) => vals2025[i] > 0 || vals2026[i] > 0);
  const active2025 = vals2025.filter((_, i) => vals2025[i] > 0 || vals2026[i] > 0);
  const active2026 = vals2026.filter((_, i) => vals2025[i] > 0 || vals2026[i] > 0);

  // Best month comparison
  const bestMonth2025 = vals2025.indexOf(Math.max(...vals2025));
  const bestMonth2026 = vals2026.indexOf(Math.max(...vals2026.filter(v => v > 0)));

  const barData = {
    labels: activeMonths,
    datasets: [
      {
        label: '2025',
        data: active2025,
        backgroundColor: 'rgba(107,114,128,0.5)',
        borderColor: '#6b7280',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: '2026',
        data: active2026,
        backgroundColor: '#3ea4b1',
        borderColor: '#3ea4b1',
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, labels: { color: TC, font: { size: 12 }, boxWidth: 12, padding: 16 } },
      tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': $' + Math.round(ctx.parsed.y).toLocaleString() } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: TC, font: { size: 11 } } },
      y: { grid: { color: GC }, ticks: { color: TC, font: { size: 11 }, callback: v => '$' + (v / 1000).toFixed(0) + 'k' } },
    },
  };

  // Delta chart — % change month over month
  const deltaLabels = activeMonths.filter((_, i) => active2025[i] > 0 && active2026[i] > 0);
  const deltaVals = activeMonths
    .filter((_, i) => active2025[i] > 0 && active2026[i] > 0)
    .map((_, i) => {
      const idx = activeMonths.findIndex((m, mi) => active2025[mi] > 0 && active2026[mi] > 0 && mi === i + (activeMonths.length - deltaLabels.length));
      return ((active2026[i] - active2025[i]) / active2025[i] * 100);
    });

  // Simpler: just compute delta for months with both values
  const combinedMonths = activeMonths.map((m, i) => ({ m, v25: active2025[i], v26: active2026[i] }))
    .filter(x => x.v25 > 0 && x.v26 > 0);

  const lineData = {
    labels: combinedMonths.map(x => x.m),
    datasets: [{
      label: 'YoY % change',
      data: combinedMonths.map(x => parseFloat(((x.v26 - x.v25) / x.v25 * 100).toFixed(1))),
      borderColor: '#1D9E75',
      backgroundColor: 'rgba(29,158,117,0.08)',
      pointBackgroundColor: combinedMonths.map(x => x.v26 >= x.v25 ? '#1D9E75' : '#dc2626'),
      pointRadius: 5,
      tension: 0.3,
      fill: true,
    }],
  };

  const lineOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => (ctx.parsed.y >= 0 ? '+' : '') + ctx.parsed.y + '%' } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: TC, font: { size: 11 } } },
      y: {
        grid: { color: GC },
        ticks: { color: TC, font: { size: 11 }, callback: v => (v >= 0 ? '+' : '') + v + '%' },
        afterDataLimits: axis => {
          const abs = Math.max(Math.abs(axis.min), Math.abs(axis.max));
          axis.min = -abs - 5; axis.max = abs + 5;
        },
      },
    },
  };

  return (
    <div>
      <div className="rep-welcome">
        <h2>Year-over-year comparison</h2>
        <p>Monthly payment totals — 2025 vs 2026, based on payment date.</p>
      </div>

      <div className="metrics">
        <MetricCard label="2025 total" value={fmtDec(total2025)} sub="Full year payments" />
        <MetricCard
          label="2026 total (YTD)"
          value={fmtDec(total2026)}
          sub="Payments to date"
          valueStyle={{ color: total2026 >= total2025 ? 'var(--green)' : 'var(--red)' }}
        />
        <MetricCard
          label="YoY change"
          value={(diff >= 0 ? '+' : '') + fmtDec(diff)}
          sub={diffPct !== '—' ? (diff >= 0 ? '+' : '') + diffPct + '% vs. 2025' : '—'}
          valueStyle={{ color: diff >= 0 ? 'var(--green)' : 'var(--red)' }}
        />
        <MetricCard label="2025 best month" value={MONTHS[bestMonth2025]} sub={fmtDec(vals2025[bestMonth2025])} />
      </div>

      <div className="chart-card" style={{ marginBottom: 16 }}>
        <div className="chart-card-title">Monthly payments — 2025 vs 2026</div>
        <div className="chart-card-sub">Total payments received each month</div>
        <div className="chart-wrap" style={{ height: 260 }}>
          <Bar data={barData} options={barOpts} />
        </div>
      </div>

      {combinedMonths.length > 0 && (
        <div className="chart-card" style={{ marginBottom: 16 }}>
          <div className="chart-card-title">Month-over-month change</div>
          <div className="chart-card-sub">% difference vs. same month in 2025</div>
          <div className="chart-wrap" style={{ height: 200 }}>
            <Line data={lineData} options={lineOpts} />
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="table-header"><span className="table-header-title">Monthly breakdown</span></div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th style={{ textAlign: 'right' }}>2025</th>
                <th style={{ textAlign: 'right' }}>2026</th>
                <th style={{ textAlign: 'right' }}>Change</th>
                <th style={{ textAlign: 'right' }}>% change</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const v25 = vals2025[i];
                const v26 = vals2026[i];
                if (v25 === 0 && v26 === 0) return null;
                const bothDone = v25 > 0 && v26 > 0;
                const delta = bothDone ? v26 - v25 : null;
                const pct = bothDone ? (delta / v25 * 100).toFixed(1) : null;
                const color = delta === null ? 'var(--text3)' : delta > 0 ? 'var(--green)' : delta < 0 ? 'var(--red)' : 'var(--text3)';
                return (
                  <tr key={m}>
                    <td style={{ fontWeight: 500 }}>{m}</td>
                    <td className="td-money" style={{ textAlign: 'right' }}>{v25 > 0 ? fmtDec(v25) : '—'}</td>
                    <td className="td-money" style={{ textAlign: 'right' }}>{v26 > 0 ? fmtDec(v26) : '—'}</td>
                    <td className="td-money" style={{ textAlign: 'right', color }}>{delta !== null ? (delta >= 0 ? '+' : '') + fmtDec(delta) : '—'}</td>
                    <td className="td-num" style={{ textAlign: 'right', color }}>{pct !== null ? (parseFloat(pct) >= 0 ? '+' : '') + pct + '%' : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rep year-over-year section */}
      <RepYoY yearOverYear={yearOverYear} user={user} isAdmin={isAdmin} />
    </div>
  );
}

const REP_LIST = [
  { id: 'peter',  name: 'Peter Povey' },
  { id: 'mitch',  name: 'Mitch Jankowski' },
  { id: 'jared',  name: 'Jared Gavard' },
  { id: 'sarah',  name: 'Sarah Sutter' },
  { id: 'dan',    name: 'Dan Pena' },
  { id: 'barry',  name: 'Barry-Lynn' },
  { id: 'justin', name: 'Justin Gavard' },
  { id: 'kelsey', name: 'Kelsey Gavard' },
];

const REP_COLORS_MAP = {
  peter: '#3ea4b1', mitch: '#7F77DD', jared: '#1D9E75', sarah: '#D4537E',
  dan: '#EF9F27', barry: '#888780', justin: '#5DCAA5', kelsey: '#F0997B',
};

function RepYoY({ yearOverYear, user, isAdmin }) {
  const { byRep2025 = {}, byRep2026 = {} } = yearOverYear || {};
  const reps = isAdmin ? REP_LIST : REP_LIST.filter(r => r.id === user?.repId);

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
        {isAdmin ? 'Rep-by-rep comparison' : 'Your year-over-year'}
      </div>
      {reps.map(rep => {
        const r25 = byRep2025[rep.id] || {};
        const r26 = byRep2026[rep.id] || {};
        const total25 = Object.values(r25).reduce((s, v) => s + v, 0);
        const total26 = Object.values(r26).reduce((s, v) => s + v, 0);
        if (total25 === 0 && total26 === 0 && isAdmin) return null;
        if (total25 === 0 && total26 === 0 && !isAdmin) return (
          <div key={rep.id} className="chart-card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{rep.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>No payment data found for 2025 or 2026 yet.</div>
          </div>
        );
        const color = REP_COLORS_MAP[rep.id] || '#3ea4b1';
        const activeMonths = MONTHS.filter((_, i) => (r25[i+1] || 0) > 0 || (r26[i+1] || 0) > 0);
        const v25arr = activeMonths.map((_, i) => r25[MONTHS.indexOf(activeMonths[i]) + 1] || 0);
        const v26arr = activeMonths.map((_, i) => r26[MONTHS.indexOf(activeMonths[i]) + 1] || 0);

        const barData = {
          labels: activeMonths,
          datasets: [
            { label: '2025', data: v25arr, backgroundColor: 'rgba(107,114,128,0.35)', borderRadius: 3, borderSkipped: false },
            { label: '2026', data: v26arr, backgroundColor: color, borderRadius: 3, borderSkipped: false },
          ],
        };
        const barOpts = {
          responsive: true, maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': $' + Math.round(ctx.parsed.y).toLocaleString() } } },
          scales: {
            x: { grid: { display: false }, ticks: { color: TC, font: { size: 10 } } },
            y: { grid: { color: GC }, ticks: { color: TC, font: { size: 10 }, callback: v => '$' + (v/1000).toFixed(0) + 'k' } },
          },
        };

        const diff = total26 - total25;
        const pct = total25 > 0 ? ((diff / total25) * 100).toFixed(1) : null;
        const diffColor = diff >= 0 ? 'var(--green)' : 'var(--red)';

        return (
          <div key={rep.id} className="chart-card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{rep.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 20, fontSize: 12 }}>
                <span style={{ color: 'var(--text2)' }}>2025: <strong style={{ color: 'var(--text)' }}>{fmtDec(total25)}</strong></span>
                <span style={{ color: 'var(--text2)' }}>2026: <strong style={{ color: 'var(--text)' }}>{fmtDec(total26)}</strong></span>
                {pct && <span style={{ color: diffColor, fontWeight: 600 }}>{diff >= 0 ? '+' : ''}{pct}%</span>}
              </div>
            </div>
            <div style={{ height: 140 }}>
              <Bar data={barData} options={barOpts} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
